import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import makeStyles from "@material-ui/core/styles/makeStyles";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import { Add, Close } from "@material-ui/icons";
import Create from "@material-ui/icons/Create";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import GridContainer from "@jumbo/components/GridContainer";
import "./style.css";

import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import TimePicker from "react-multi-date-picker/plugins/time_picker";

import CmtCard from "@coremat/CmtCard";
import CmtCardContent from "@coremat/CmtCard/CmtCardContent";
import MiniQuizes from "./miniQuizes";
import { fetchError } from "redux/actions";
import TestsRequests from "services/tests";

import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
  },
  cardRoot: {
    paddingTop: "24px",
    [theme.breakpoints.down("xs")]: {
      "& .Cmt-header-root": {
        flexDirection: "column",
      },
      "& .Cmt-action-default-menu": {
        marginLeft: 0,
        marginTop: 10,
      },
    },
  },
  cardContentRoot: {
    padding: 0,
    paddingInline: "25px",
  },
  formContainer: {
    width: "100%",
  },
  girdContainer: {
    width: "100%",
    margin: "0",
    padding: "0",
  },
  customInput: {
    width: "80%",
    [theme.breakpoints.down("lg")]: {
      width: "100%",
    },
  },
  inputContainer: {
    paddingInline: "40px !important",
    "& p": {
      fontWeight: "bold",
      marginBottom: "16px",
    },
  },
  buttonContainer: {
    paddingInline: "40px !important",
    justifyContent: "flex-end",
  },
  inputTitle: {
    marginBottom: "20px",
  },
  select: {
    height: "56px",
    "& .MuiOutlinedInput-input": {
      height: "25px",
    },
  },
  previewContainer: {
    width: "100%",
    paddingLeft: "50%",
    display: "flex",
    justifyContent: "center",
    "& img": {
      width: "190px",
    },
  },
  removeButton: {
    cursor: "pointer",
  },
}));

const TestFields = () => {
  const now = new Date();
  now.setHours(18, 0, 0, 0);

  const [testTitle, setTestTitle] = useState("");
  const [testDesc, setTestDesc] = useState("");
  const [fromDate, setFromDate] = useState({
    date: now,
    gregorian: new DateObject({ date: now, format: "HH:mm:ss   YYYY/MM/DD" })
      .convert(gregorian, gregorian_en)
      .format(),
  });
  const [toDate, setToDate] = useState({
    date: now,
    gregorian: new DateObject({ date: now, format: "HH:mm:ss   YYYY/MM/DD" })
      .convert(gregorian, gregorian_en)
      .format(),
  });
  const [testTime, setTestTime] = useState(10);
  const [testMaxAnswer, setTestMaxAnswer] = useState(10);
  const [minScore, setMinScore] = useState(-1);
  const [archived, setArchived] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [miniGames, setMiniGames] = useState([]);

  const { miniGamesList, testById } = useSelector(({ tests }) => tests);

  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams();

  useEffect(() => {
    if (testById) {
      if (testById._id === id) {
        setTestTitle(testById.title);
        setTestDesc(testById.description);
        setFromDate({
          date: testById.startDate,
          gregorian: new DateObject({
            date: testById.startDate,
            format: "HH:mm:ss   YYYY/MM/DD",
          })
            .convert(gregorian, gregorian_en)
            .format(),
        });
        setToDate({
          date: testById.endDate,
          gregorian: new DateObject({
            date: testById.endDate,
            format: "HH:mm:ss   YYYY/MM/DD",
          })
            .convert(gregorian, gregorian_en)
            .format(),
        });
        setTestTime(+testById.quizDuration / 60000);
        setTestMaxAnswer(testById.maxAnswer);
        setArchived(testById.archived);
        loadQuizes();
        if (testById.hasOwnProperty("maxScoreForParticipation")) {
          setMinScore(testById.maxScoreForParticipation);
        }
        if (testById.hasOwnProperty("logo")) {
          setPreviewImage(testById.logo);
        }
      }
    }
  }, [testById]);

  async function loadQuizes() {
    try {
      const quizData = await TestsRequests.generateMiniQuizes(
        testById.miniGames
      );
      setMiniGames(quizData);
    } catch (error) {
      console.log("Error loading quizzes:", error);
    }
  }

  useEffect(() => {
    if (id) {
      dispatch(TestsRequests.getQuizById(id));
    }
  }, [id]);

  const convertFromDate = (date, format = fromDate.format) => {
    let object = { date, format };
    setFromDate({
      gregorian: new DateObject(object)
        .convert(gregorian, gregorian_en)
        .format(),
      ...object,
    });
  };
  const convertToDate = (date, format = toDate.format) => {
    let object = { date, format };
    setToDate({
      gregorian: new DateObject(object)
        .convert(gregorian, gregorian_en)
        .format(),
      ...object,
    });
  };

  const convertToStandardFormat = (dateString) => {
    const [time, date] = dateString.split("   ");
    const [hours, minutes, seconds] = time.split(":");
    const [year, month, day] = date.split("/");
    const standardFormat = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    const dateObject = new Date(standardFormat);
    const isoString = dateObject.toISOString();
    return isoString;
  };

  const handleCreateTest = () => {
    if (
      testTitle &&
      fromDate.date &&
      toDate.date &&
      testTime &&
      testMaxAnswer
    ) {
      const matchData = {
        title: testTitle,
        description: testDesc,
        startDate: convertToStandardFormat(fromDate.gregorian),
        endDate: convertToStandardFormat(toDate.gregorian),
        quizDuration: +testTime * 60000,
        maxAnswer: testMaxAnswer,
        maxScoreForParticipation: minScore,
        archived: archived,
      };
      if (previewImage !== "") {
        matchData["logo"] = previewImage;
      }
      if (miniGamesList.lenght !== 0) {
        const temp = [];
        miniGamesList.forEach((element) => {
          temp.push(element.id);
        });
        matchData["miniGames"] = temp;
      }
      dispatch(TestsRequests.createTest(matchData));
    } else {
      dispatch(fetchError("موارد لازم را به درستی وارد نمایید"));
    }
  };

  const handleUpdateTest = () => {
    if (
      testTitle &&
      fromDate.date &&
      toDate.date &&
      testTime &&
      testMaxAnswer
    ) {
      const matchData = {
        title: testTitle,
        description: testDesc,
        startDate: convertToStandardFormat(fromDate.gregorian),
        endDate: convertToStandardFormat(toDate.gregorian),
        quizDuration: +testTime * 60000,
        maxAnswer: testMaxAnswer,
        maxScoreForParticipation: minScore,
        archived: archived,
      };
      if (previewImage !== "") {
        matchData["logo"] = previewImage;
      }
      if (miniGamesList.lenght !== 0) {
        const temp = [];
        miniGamesList.forEach((element) => {
          temp.push(element.id);
        });
        matchData["miniGames"] = temp;
      }
      dispatch(TestsRequests.updateQuizById(id, matchData));
    } else {
      dispatch(fetchError("موارد لازم را به درستی وارد نمایید"));
    }
  };

  return (
    <CmtCard className={classes.cardRoot}>
      <CmtCardContent className={classes.cardContentRoot}>
        <form className={classes.formContainer} noValidate autoComplete="off">
          <GridContainer className={classes.girdContainer}>
            <Grid item xs={12}>
              <TextField
                id="quizNameField"
                label="نام آزمون"
                variant="outlined"
                className={classes.customInput}
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="nationalCodeInput"
                multiline
                minRows={4}
                label="توضیحات"
                variant="outlined"
                className={classes.customInput}
                value={testDesc}
                onChange={(e) => setTestDesc(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display={"flex"} alignItems="center">
                <CalendarTodayIcon fontSize="small" />
                <Box marginLeft={2}>
                  <Typography>تاریخ شروع</Typography>
                </Box>
              </Box>

              <DatePicker
                containerClassName="custom-container"
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                value={fromDate.date}
                onChange={convertFromDate}
                format="HH:mm:ss   YYYY/MM/DD"
                plugins={[<TimePicker position="bottom" />]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display={"flex"} alignItems="center">
                <CalendarTodayIcon fontSize="small" />
                <Box marginLeft={2}>
                  <Typography>تاریخ پایان</Typography>
                </Box>
              </Box>
              <DatePicker
                containerClassName="custom-container"
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                value={toDate.date}
                type="Input"
                onChange={convertToDate}
                format="HH:mm:ss   YYYY/MM/DD"
                plugins={[<TimePicker position="bottom" />]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.inputTitle}>
                {`زمان کوییز(دقیقه)`}
              </Typography>
              <TextField
                id="quizTimeField"
                label="زمان کوییز"
                variant="outlined"
                type="number"
                className={classes.customInput}
                value={testTime}
                onChange={(e) => setTestTime(e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.inputTitle}>
                حداکثر سوال جهت پاسخ
              </Typography>
              <TextField
                id="quizMaxAnser"
                label="حداکثر سوال جهت پاسخ"
                variant="outlined"
                type="number"
                className={classes.customInput}
                value={testMaxAnswer}
                onChange={(e) => setTestMaxAnswer(e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.inputTitle}>
                حداکثر پله جهت شرکت در آزمون
              </Typography>
              {minScore !== -1 ? (
                <TextField
                  id="personalCodeInput"
                  label="حداکثر پله"
                  variant="outlined"
                  placeholder="حداکثر پله"
                  type="number"
                  className={classes.customInput}
                  value={id ? minScore : minScore === 0 ? undefined : minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                />
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={minScore === -1}
                      onChange={() => setMinScore("")}
                      name="check-score"
                    />
                  }
                  label="بدون محدودیت"
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.inputTitle}>تصویر</Typography>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="selectImage-label">تصویر</InputLabel>
                <Select
                  labelId="selectImage-label"
                  id="selectImage"
                  value={previewImage}
                  onChange={(e) => setPreviewImage(e.target.value)}
                  className={classes.select}
                  label="نوع کاربر"
                >
                  <MenuItem
                    value={
                      "https://app.rahebehesht.com/wp-content/uploads/2023/12/joftpooch-logo2.png"
                    }
                  >
                    <img
                      src="https://app.rahebehesht.com/wp-content/uploads/2023/12/joftpooch-logo2.png"
                      width={"50px"}
                      alt="first-item"
                    />
                  </MenuItem>
                  <MenuItem
                    value={
                      "https://app.rahebehesht.com/wp-content/uploads/2023/12/خزانه-سوالات-1.svg"
                    }
                  >
                    <img
                      src="https://app.rahebehesht.com/wp-content/uploads/2023/12/خزانه-سوالات-1.svg"
                      width={"50px"}
                      alt="second-item"
                    />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {previewImage && (
              <Box className={classes.previewContainer}>
                <Box display={"flex"} flexDirection={"column"}>
                  <Close
                    className={classes.removeButton}
                    onClick={() => setPreviewImage("")}
                  />
                  <img src={previewImage} alt="preview" />
                </Box>
              </Box>
            )}
            <Grid item xs={12} md={6}>
              <Typography className={classes.inputTitle}>خزانه</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={archived}
                    onChange={() => setArchived((prev) => !prev)}
                    name="archive"
                  />
                }
                label="خزانه سوال"
              />
            </Grid>
            <MiniQuizes receivedQuizes={miniGames} />
            <Grid item xs={12} container className={classes.buttonContainer}>
              {id ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<Create />}
                  onClick={handleUpdateTest}
                >
                  ویرایش آزمون
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<Add />}
                  onClick={handleCreateTest}
                >
                  ساخت آزمون
                </Button>
              )}
            </Grid>
          </GridContainer>
        </form>
      </CmtCardContent>
    </CmtCard>
  );
};

export default TestFields;
